import { useEffect, useMemo, useRef, useState } from "react";
import { SearchSelect } from "../../../shared/components/SearchSelect";
import { overlayClose } from "../../../shared/utils/overlayClose";
import { getAreas, getDisponibilidad, crearReservacion } from "../services/portalService";
import DatosPagoFields, { labelSt, fldStyle, hoyStr, pagoInicial, validarPago, pagoPayload } from "./DatosPagoFields";

/**
 * NuevaReservacionModal
 * ─────────────────────────────────────────────────────────────────────────────
 * El admin registra una reservación a nombre de una filial (el residente llamó
 * o pasó por la oficina). Queda PENDIENTE como las que hacen los residentes, o
 * aprobada de una vez si ya pagó (genera factura + recibo en la cuenta
 * bancaria elegida).
 *
 * Props:
 *   filiales → lista ya cargada por AdminReservaciones
 *   onClose  → cerrar sin guardar
 *   onSaved  → fn(reservacion) al registrar
 */

function precioTexto(area) {
  if (!area) return "";
  const partes = [];
  if (area.precio_colones) partes.push(`₡${Number(area.precio_colones).toLocaleString("en-US", { minimumFractionDigits: 2 })}`);
  if (area.precio_dolares) partes.push(`$${Number(area.precio_dolares).toLocaleString("en-US", { minimumFractionDigits: 2 })}`);
  return partes.join(" / ");
}

const seccionSt = {
  fontSize: 12, fontWeight: 800, color: "#f97316", textTransform: "uppercase",
  letterSpacing: 0.6, display: "flex", alignItems: "center", gap: 7, margin: 0,
};

export default function NuevaReservacionModal({ filiales, onClose, onSaved }) {
  const [areas,        setAreas]        = useState([]);
  const [filialId,     setFilialId]     = useState("");
  const [areaId,       setAreaId]       = useState("");
  const [fecha,        setFecha]        = useState(hoyStr());
  const [horarioId,    setHorarioId]    = useState("");
  const [ocupados,     setOcupados]     = useState([]);   // disponibilidad del mes de `fecha`
  const [cargandoDisp, setCargandoDisp] = useState(false);
  const [notas,        setNotas]        = useState("");
  const [comprobante,  setComprobante]  = useState(null);
  const [aprobar,      setAprobar]      = useState(false);
  const [pago,         setPago]         = useState(pagoInicial);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState("");
  const fileRef = useRef();

  useEffect(() => {
    getAreas().then(d => setAreas((d.results || d).filter(a => a.activa))).catch(() => {});
  }, []);

  const area     = areas.find(a => String(a.id) === String(areaId)) || null;
  const horarios = useMemo(() => area?.horarios?.filter(h => h.activo) || [], [area]);

  // La disponibilidad se pide por mes: se recarga al cambiar de área o de mes.
  const mesKey = fecha ? fecha.slice(0, 7) : "";
  useEffect(() => {
    if (!areaId || !mesKey) { setOcupados([]); return; }
    const [y, m] = mesKey.split("-");
    let vigente = true;
    setCargandoDisp(true);
    getDisponibilidad(areaId, Number(y), Number(m))
      .then(d => { if (vigente) setOcupados(d); })
      .catch(() => { if (vigente) setOcupados([]); })
      .finally(() => { if (vigente) setCargandoDisp(false); });
    return () => { vigente = false; };
  }, [areaId, mesKey]);

  function ocupadoPor(hId) {
    return ocupados.find(o => o.fecha === fecha && o.horario === hId) || null;
  }

  // Si el horario elegido quedó ocupado al cambiar la fecha o el área, se suelta.
  useEffect(() => {
    if (horarioId && (!horarios.some(h => h.id === horarioId) || ocupadoPor(horarioId))) setHorarioId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, areaId, ocupados, horarios]);

  const opcionesFilial = useMemo(() => filiales.map(f => ({
    id: f.id,
    primary: `Filial ${f.idFilial}`,
    secondary: f.propietario_nombre || "",
  })), [filiales]);

  async function handleGuardar() {
    setError("");
    if (!filialId)  { setError("Seleccioná la filial que hace la reservación."); return; }
    if (!areaId)    { setError("Seleccioná el área."); return; }
    if (!fecha)     { setError("Indicá la fecha de la reservación."); return; }
    if (!horarioId) { setError("Seleccioná un horario disponible."); return; }
    if (aprobar) {
      const e = validarPago(pago);
      if (e) { setError(e); return; }
    }

    const fd = new FormData();
    fd.append("filial", filialId);
    fd.append("area", areaId);
    fd.append("horario", horarioId);
    fd.append("fecha", fecha);
    fd.append("notas", notas);
    if (comprobante) fd.append("comprobante_pago", comprobante);
    if (aprobar) {
      fd.append("aprobar", "true");
      Object.entries(pagoPayload(pago)).forEach(([k, v]) => fd.append(k, v));
    }

    setSaving(true);
    try {
      const r = await crearReservacion(fd);
      onSaved(r);
    } catch (e) {
      try { const d = JSON.parse(e.message); setError(Object.values(d).flat().join(". ")); }
      catch { setError(e.message || "No se pudo registrar la reservación."); }
    } finally { setSaving(false); }
  }

  const esPasada = fecha && fecha < hoyStr();

  return (
    <div {...overlayClose(onClose)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(4px)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 16, width: "min(980px, 96vw)",
        boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
        maxHeight: "92vh", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border-card)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "var(--text-primary)" }}>
              Nueva reservación
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
              Registrá una reservación a nombre de una filial
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 18 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Body: dos columnas en pantallas anchas, una en celular */}
        <div style={{
          padding: "20px 24px", overflowY: "auto",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "20px 28px", alignItems: "start",
        }}>
          {/* ── Columna izquierda: qué, quién y cuándo ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={seccionSt}><i className="fa-solid fa-calendar-day" /> Reservación</p>

            <div>
              <label style={labelSt}>Filial que reserva *</label>
              <SearchSelect
                options={opcionesFilial}
                value={filialId}
                onChange={id => setFilialId(id)}
                placeholder="Buscar filial o propietario…"
                icon="fa-house"
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelSt}>Área *</label>
                <select value={areaId} onChange={e => setAreaId(e.target.value)} style={fldStyle({ cursor: "pointer" })}>
                  <option value="">Seleccioná un área</option>
                  {areas.map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </select>
              </div>
              <div>
                <label style={labelSt}>Fecha *</label>
                <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={fldStyle()} />
              </div>
            </div>
            {area && precioTexto(area) && (
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: -8 }}>
                <i className="fa-solid fa-tag" style={{ marginRight: 5, color: "#f97316" }} />
                Precio del área: <strong style={{ color: "var(--text-primary)" }}>{precioTexto(area)}</strong>
              </div>
            )}
            {esPasada && (
              <div style={{ fontSize: 12, color: "#f59e0b", marginTop: -8 }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 5 }} />
                La fecha ya pasó: se registra igual, por ejemplo para cargar una reserva atrasada.
              </div>
            )}

            <div>
              <label style={labelSt}>
                Horario *
                {cargandoDisp && <i className="fa-solid fa-circle-notch fa-spin" style={{ marginLeft: 6 }} />}
              </label>
              {!area ? (
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>
                  Elegí un área para ver sus horarios.
                </p>
              ) : horarios.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: "var(--text-secondary)" }}>
                  Esta área no tiene horarios activos. Agregalos en Áreas Comunes.
                </p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {horarios.map(h => {
                    const ocup = ocupadoPor(h.id);
                    const sel  = horarioId === h.id;
                    return (
                      <button key={h.id} type="button" disabled={!!ocup} onClick={() => setHorarioId(h.id)} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                        padding: "10px 14px", borderRadius: 10, textAlign: "left",
                        cursor: ocup ? "not-allowed" : "pointer",
                        border: `1px solid ${sel ? "#f97316" : ocup ? "var(--border-card)" : "rgba(74,222,128,0.3)"}`,
                        background: sel ? "rgba(249,115,22,0.12)" : ocup ? "var(--hover-bg)" : "rgba(74,222,128,0.06)",
                        opacity: ocup ? 0.7 : 1,
                      }}>
                        <span>
                          <span style={{ display: "block", fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{h.nombre}</span>
                          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                            <i className="fa-solid fa-clock" style={{ marginRight: 4, fontSize: 10 }} />
                            {h.hora_inicio?.slice(0, 5)} - {h.hora_fin?.slice(0, 5)}
                          </span>
                        </span>
                        {ocup ? (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#f87171", textAlign: "right" }}>
                            Ocupado - Filial {ocup.filial} ({ocup.estado.toLowerCase()})
                          </span>
                        ) : sel ? (
                          <i className="fa-solid fa-circle-check" style={{ color: "#f97316" }} />
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#4ade80" }}>Disponible</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Columna derecha: detalles y pago ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={seccionSt}><i className="fa-solid fa-receipt" /> Detalles y pago</p>

            <div>
              <label style={labelSt}>Notas (opcional)</label>
              <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
                placeholder="Ej. cantidad de personas, tipo de actividad…"
                style={fldStyle({ resize: "vertical" })} />
            </div>

            <div>
              <label style={labelSt}>Comprobante de pago (opcional)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <button type="button" onClick={() => fileRef.current?.click()} style={{
                  background: "var(--hover-bg)", border: "1px solid var(--border-card)",
                  color: "var(--text-primary)", borderRadius: 8, padding: "7px 14px",
                  cursor: "pointer", fontSize: 12, fontWeight: 600,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <i className="fa-solid fa-paperclip" /> {comprobante ? "Cambiar archivo" : "Adjuntar archivo"}
                </button>
                {comprobante && (
                  <span style={{ fontSize: 12, color: "var(--text-secondary)", display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 240 }}>{comprobante.name}</span>
                    <button type="button" title="Quitar" onClick={() => { setComprobante(null); if (fileRef.current) fileRef.current.value = ""; }}
                      style={{ background: "none", border: "none", color: "#f87171", cursor: "pointer", padding: 0 }}>
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </span>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*,.pdf"
                onChange={e => setComprobante(e.target.files[0] || null)} style={{ display: "none" }} />
            </div>

            <div style={{
              border: `1px solid ${aprobar ? "rgba(34,197,94,0.35)" : "var(--border-card)"}`,
              background: aprobar ? "rgba(34,197,94,0.06)" : "transparent",
              borderRadius: 10, padding: "12px 14px",
            }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                <input type="checkbox" checked={aprobar} onChange={e => setAprobar(e.target.checked)} style={{ marginTop: 3, accentColor: "#22c55e" }} />
                <span>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>
                    Ya pagó: aprobar de una vez
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    Genera la factura y el recibo de la filial en la cuenta bancaria elegida. Si no se marca, queda pendiente para revisarla después.
                  </span>
                </span>
              </label>
              {aprobar && (
                <div style={{ marginTop: 14 }}>
                  <DatosPagoFields pago={pago} onChange={p => { setPago(prev => ({ ...prev, ...p })); setError(""); }} area={area} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border-card)", display: "flex", flexDirection: "column", gap: 12 }}>
          {error && (
            <div style={{
              background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)",
              borderLeft: "3px solid #f87171", borderRadius: 8,
              padding: "10px 14px", color: "#f87171", fontSize: 13,
            }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={{
              padding: "9px 18px", borderRadius: 8, border: "1px solid var(--border-card)",
              background: "transparent", color: "var(--text-secondary)", cursor: "pointer", fontSize: 14,
            }}>
              Cancelar
            </button>
            <button onClick={handleGuardar} disabled={saving} style={{
              padding: "9px 22px", borderRadius: 8, border: "none",
              background: aprobar ? "#22c55e" : "#f97316", color: "#fff", fontWeight: 700, fontSize: 14,
              cursor: saving ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> Guardando…</>
                : <><i className="fa-solid fa-calendar-plus" /> {aprobar ? "Registrar y aprobar" : "Registrar reservación"}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
